/**
 * rows: [{ date, table, period, type, side, vendor, total }]
 * lookup[type][side][vendor][date] = total
 *
 * Date separation:
 *   allDates     = all dates returned by backend (display + median reference window)
 *   displayDates = only the dates the user wants to see (>= displayFrom)
 *
 * Median reference window:
 *   Each date's vendor median is computed from the `medianWindow` calendar
 *   days immediately BEFORE that date — anchored to the date itself, never
 *   to "today" or to the start of the visible period (see getDateReferenceWindow).
 *   This guarantees a day's classification is stable over time: it only
 *   changes if data belonging to its own reference window gets corrected,
 *   never simply because time passes or new data is imported for other days.
 */

export function buildLookup(rows) {
  const lookup = {}
  rows.forEach(r => {
    if (!lookup[r.type])                   lookup[r.type] = {}
    if (!lookup[r.type][r.side])           lookup[r.type][r.side] = {}
    if (!lookup[r.type][r.side][r.vendor]) lookup[r.type][r.side][r.vendor] = {}
    lookup[r.type][r.side][r.vendor][r.date] = r.total
  })
  return lookup
}

export function getUniqueDates(rows) {
  return [...new Set(rows.map(r => r.date))].sort()
}

/**
 * Generates a complete array of dates from start to end (inclusive),
 * filling gaps where no data exists.
 */
export function generateDateRange(from, to) {
  if (!from || !to) return []
  const dates = []
  const cur   = new Date(from)
  const end   = new Date(to)
  while (cur <= end) {
    dates.push(cur.toISOString().slice(0, 10))
    cur.setDate(cur.getDate() + 1)
  }
  return dates
}

export function getDisplayDates(rows, displayFrom, displayTo) {
  // Generate full date range from displayFrom to displayTo-1 (exclude today)
  if (displayFrom && displayTo) {
    const oneDayBefore = new Date(displayTo)
    oneDayBefore.setDate(oneDayBefore.getDate() - 1)
    return generateDateRange(displayFrom, oneDayBefore.toISOString().slice(0, 10))
  }
  return getUniqueDates(rows)
}

/**
 * Returns the `windowSize` calendar dates immediately before `date` (ascending,
 * exclusive of `date` itself). This is the reference window used to compute a
 * vendor's median for that specific date — anchored to the date being
 * evaluated, not to "today" or to the start of the visible period. That way,
 * a day's median (and therefore its status) doesn't drift just because the
 * calendar advances or new data is imported for other days.
 */
export function getDateReferenceWindow(date, windowSize) {
  const dates = []
  const base  = new Date(date)
  for (let i = windowSize; i >= 1; i--) {
    const d = new Date(base)
    d.setDate(d.getDate() - i)
    dates.push(d.toISOString().slice(0, 10))
  }
  return dates
}

export function getUniqueTypes(rows) {
  return [...new Set(rows.map(r => r.type))].sort()
}

export function getUniqueSides(rows) {
  return [...new Set(rows.map(r => r.side))].sort()
}

export function getUniqueVendors(rows) {
  return [...new Set(rows.map(r => r.vendor))].sort()
}

function median(values) {
  if (!values.length) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid    = Math.floor(sorted.length / 2)
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2
}

/**
 * Pre-compute vendor medians for a given type+side, using only the supplied
 * reference dates (see getDateReferenceWindow — callers pass a window
 * anchored to the date being evaluated).
 */
export function computeVendorMedians(lookup, type, side, refDates) {
  const byVendor = lookup?.[type]?.[side]
  if (!byVendor) return {}
  const result = {}
  Object.keys(byVendor).forEach(vendor => {
    const vals = refDates
      .map(d => byVendor[vendor]?.[d] ?? 0)
      .filter(v => v > 0)
    result[vendor] = median(vals)
  })
  return result
}

/**
 * Cell status for a (type, side, date) in the display window.
 * Median is computed from the `medianWindow` days immediately before `date`
 * (see getDateReferenceWindow) — anchored to this specific date, not to the
 * start of the display window or to "today".
 *
 * Returns:
 *   'all'     → all expected vendors have data at normal volume
 *   'low'     → all vendors present but at least one below threshold
 *   'partial' → some vendors absent
 *   'none'    → all vendors absent
 */
export function cellStatus(lookup, type, side, date, allDates, threshold = 80, medianWindow = 30) {
  const byVendor = lookup?.[type]?.[side]
  if (!byVendor) return { status: 'none', present: [], missing: [], lowVolume: [] }

  const medianDates = getDateReferenceWindow(date, medianWindow)
  const medians      = computeVendorMedians(lookup, type, side, medianDates)

  // Expected vendors = those that ever had data across ALL dates
  const expectedVendors = Object.keys(byVendor).filter(vendor =>
    allDates.some(d => (byVendor[vendor]?.[d] ?? 0) > 0)
  )
  if (!expectedVendors.length) return { status: 'none', present: [], missing: [], lowVolume: [] }

  const absent    = []
  const lowVolume = []
  const ok        = []

  expectedVendors.forEach(vendor => {
    const val = byVendor[vendor]?.[date] ?? 0
    const med = medians[vendor] ?? 0
    if (val === 0) {
      absent.push(vendor)
    } else if (med > 0 && val < med * (threshold / 100)) {
      lowVolume.push(vendor)
    } else {
      ok.push(vendor)
    }
  })

  let status
  if (absent.length === expectedVendors.length)  status = 'none'
  else if (absent.length > 0)                    status = 'partial'
  else if (lowVolume.length > 0)                 status = 'low'
  else                                           status = 'all'

  return { status, present: ok, missing: absent, lowVolume }
}

export function colorForStatus(status) {
  if (status === 'all')     return '#2e7d32'  // green  — all ok
  if (status === 'low')     return '#f9a825'  // yellow — low volume
  if (status === 'partial') return '#ef6c00'  // orange — some vendors missing
  return '#c62828'                            // red    — no data (worst case)
}

/**
 * vendorSideDetail — same logic as cellStatus but per vendor.
 * Median is computed from the `medianWindow` days immediately before `date`.
 */
export function vendorSideDetail(lookup, type, side, vendor, date, allDates, threshold = 80, medianWindow = 30) {
  const byDate = lookup?.[type]?.[side]?.[vendor]
  if (!byDate) return null

  const everHadData = allDates.some(d => (byDate[d] ?? 0) > 0)
  if (!everHadData) return null

  const medianDates = getDateReferenceWindow(date, medianWindow)
  const vals        = medianDates.map(d => byDate[d] ?? 0).filter(v => v > 0)
  const med         = median(vals)

  const total   = byDate[date] ?? 0
  const hasData = total > 0
  const isLow   = hasData && med > 0 && total < med * (threshold / 100)

  return { vendor, side, hasData, isLow, total, median: Math.round(med) }
}

/**
 * Global metrics — median per-date (anchored to each date via medianWindow),
 * displayDates for gap detection.
 */
export function computeMetrics(rows, threshold = 80, displayFrom = null, displayTo = null, medianWindow = 30) {
  if (!rows.length) return {}

  const lookup       = buildLookup(rows)
  const allDates     = getUniqueDates(rows)
  const displayDates = getDisplayDates(rows, displayFrom, displayTo)
  const types        = getUniqueTypes(rows)
  const sides        = getUniqueSides(rows)

  const gapDayDates = displayDates.filter(date =>
    types.some(type =>
      sides.some(side => {
        const { status } = cellStatus(lookup, type, side, date, allDates, threshold, medianWindow)
        return status !== 'all'
      })
    )
  )

  const globalPct = Math.round((displayDates.length - gapDayDates.length) / Math.max(displayDates.length, 1) * 100)

  const dayTotals = displayDates.map(date => {
    let total = 0
    types.forEach(type => sides.forEach(side => {
      const byVendor = lookup?.[type]?.[side] ?? {}
      Object.values(byVendor).forEach(byDate => { total += byDate[date] ?? 0 })
    }))
    return { date, total }
  })
  const bestDay = [...dayTotals].sort((a, b) => b.total - a.total)[0]?.date ?? '—'

  const allVendors = [...new Set(rows.map(r => r.vendor))]
  const vendorMiss = allVendors.map(vendor => {
    let miss = 0
    displayDates.forEach(date => {
      types.forEach(type => sides.forEach(side => {
        const detail = vendorSideDetail(lookup, type, side, vendor, date, allDates, threshold, medianWindow)
        if (detail && (!detail.hasData || detail.isLow)) miss++
      }))
    })
    return { vendor, miss }
  }).sort((a, b) => b.miss - a.miss)

  return {
    globalPct,
    gapDays:         gapDayDates.length,
    gapDayDates,
    worstVendor:     vendorMiss[0]?.vendor ?? '—',
    worstVendorMiss: vendorMiss[0]?.miss   ?? 0,
    bestDay,
  }
}